import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { LIVE_EXAMPLES, type LiveExampleKey } from '@/config';
import type { AuthPolicyMode } from '@/types';

/**
 * "See it live" link (task 540, report Pillar 4).
 *
 * Points the User at the working demo that matches what they are configuring.
 * The demos are examples running on an unaffiliated domain (raideil.com),
 * consuming real Innovate endpoints exactly the way a customer's application
 * would — that framing is the copy, not a disclaimer bolted on afterwards.
 *
 * Live demos only: the innovate-examples source repository is private until
 * the go-public backlog task 496 executes (see the `sourceUrl` TODO on
 * `LiveExample` in `src/config/index.ts`).
 */

/**
 * The demo that matches an endpoint's auth mode: browser-only `public` calls
 * are what the widget site proves, server-side `apiKey` calls are what the
 * shop proves. `jwt` and `custom` have no demo yet, so nothing is shown rather
 * than a mismatched one.
 */
export function demoForAuthMode(mode: AuthPolicyMode | null | undefined): LiveExampleKey | null {
  if (mode === 'public') return 'widget';
  if (mode === 'apiKey') return 'shop';
  return null;
}

interface SeeItLiveProps {
  /** Which demo to show. */
  example: LiveExampleKey;
  /** Test hook for the wrapper. Defaults to `see-it-live-<example>`. */
  testId?: string;
  className?: string;
}

export default function SeeItLive({ example, testId, className = '' }: SeeItLiveProps) {
  const demo = LIVE_EXAMPLES[example];
  const wrapperTestId = testId ?? `see-it-live-${example}`;

  return (
    <div
      data-testid={wrapperTestId}
      className={`flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 ${className}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
          See it live
        </p>
        <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">{demo.name}</p>
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{demo.pattern}</p>
      </div>
      <a
        href={demo.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${wrapperTestId}-link`}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
      >
        Open the demo
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </a>
      <p className="w-full text-xs text-gray-400 dark:text-gray-500">
        An example application on an unaffiliated domain, calling real Innovate endpoints the way
        your own application would.
      </p>
    </div>
  );
}
